import { ReportController } from "../../controllers/report-controller";
import { Planets } from "../../planets";
import { Substructure } from "../../substructure";
import type { Chart, ChartData, JsonValue, PlanetPosition } from "../chart";
import { Report, type ReportData } from "../report";

interface PlanetSubstructure {
  planet: string;
  gate: number;
  line: number;
  color: number;
  tone: number;
  base: number;
}

interface SubstructureReportData {
  [key: string]: JsonValue;
  /** Birth coordinates → IANA zone; date/time below are local wall clock there (UTC for ephemeris is derived from this). */
  birthContext: {
    ianaTimeZone: string;
    birthLocalDate: string;
    birthLocalTime: string;
    designLocalDateTime: string;
  };
  Personality: PlanetSubstructure[];
  Design: PlanetSubstructure[];
}

function generateSubstructureInformationForPlanets(
  chart: Chart,
  key: "Personality" | "Design",
): PlanetSubstructure[] {
  const section = chart[key] as ChartData | undefined;
  if (!section) {
    throw new Error(`Chart is missing ${key} data`);
  }

  const planetData: PlanetSubstructure[] = [];

  Planets.all.forEach((planet: string) => {
    const chartPlanet = section[planet] as PlanetPosition;
    const substructureData =
      chartPlanet.decimalDegrees != null
        ? Substructure.getFromDecimal(chartPlanet.sign, chartPlanet.decimalDegrees)
        : Substructure.get(
            chartPlanet.sign,
            chartPlanet.degree,
            chartPlanet.minutes,
            chartPlanet.seconds,
          );

    planetData.push({
      planet: planet,
      gate: substructureData.gate,
      line: substructureData.line,
      color: substructureData.color,
      tone: substructureData.tone,
      base: substructureData.base,
    });
  });

  return planetData;
}

const report = new Report({
  title: "Full Substructure Report",
  run: function (chart: Chart): string {
    const reportData: SubstructureReportData = {
      birthContext: {
        ianaTimeZone: (chart.birthTimeZone as string | undefined) ?? "",
        birthLocalDate: (chart.birthDate as string | undefined) ?? "",
        birthLocalTime: (chart.birthTime as string | undefined) ?? "",
        designLocalDateTime: (chart.designDate as string | undefined) ?? "",
      },
      Personality: generateSubstructureInformationForPlanets(chart, "Personality"),
      Design: generateSubstructureInformationForPlanets(chart, "Design"),
    };

    // Populate data object
    (this as Report).data = reportData as ReportData;

    // Return JSON string
    return JSON.stringify(reportData, null, 2);
  },
});

ReportController.addReport(report);
